import React from 'react';
import { ShieldOff, Eye } from 'lucide-react';

/**
 * Wrap a page or section with this to enforce read-only mode.
 *
 * Props:
 *   hasAccess  — if false, show "No Access" block
 *   canWrite   — if false but hasAccess is true, shows read-only banner
 *   children   — page content
 */
export function PermissionGuard({ hasAccess, canWrite, children }) {
  if (!hasAccess) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[300px] text-center p-8">
        <div className="w-16 h-16 bg-red-100 rounded-2xl flex items-center justify-center mb-4">
          <ShieldOff className="w-8 h-8 text-red-400" />
        </div>
        <h2 className="text-lg font-bold text-gray-700 mb-2">Access Denied</h2>
        <p className="text-gray-400 text-sm max-w-xs">
          You do not have permission to access this page. Please contact your administrator.
        </p>
      </div>
    );
  }

  return (
    <>
      {!canWrite && (
        <div className="mb-4 flex items-center gap-2 px-4 py-2.5 bg-blue-50 border border-blue-200 rounded-xl text-sm text-blue-700">
          <Eye className="w-4 h-4 flex-shrink-0" />
          <span>
            <strong>Read-only mode:</strong> You can view this data but cannot make any changes.
          </span>
        </div>
      )}
      {children}
    </>
  );
}

/**
 * Conditionally renders children only if user has write (or higher) permission.
 * Use this to hide Add/Edit/Delete buttons in read-only mode.
 *
 * Usage:
 *   <CanWrite canWrite={canWrite}>
 *     <button>Add New</button>
 *   </CanWrite>
 */
export function CanWrite({ canWrite, children }) {
  if (!canWrite) return null;
  return children;
}

export function CanEdit({ canEdit, children }) {
  if (!canEdit) return null;
  return children;
}

export function CanDelete({ canDelete, children }) {
  if (!canDelete) return null;
  return children;
}
